import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { catchError, of } from 'rxjs';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { environment } from 'src/environments/environment';
import { TokenApiService } from 'src/app/core/services/token-api.service';
import { CollectionApiService } from 'src/app/core/services/collection-api.service';
import { PlaygroundHistoryEntry, PlaygroundHistoryService } from 'src/app/core/services/playground-history.service';
import { ToastService } from 'src/app/core/services/toast.service';
import { DeveloperApiToken, PermissionLevel, ScopeFeature } from 'src/app/core/domain/developer-token.model';
import { PlaygroundCollection, PlaygroundRequestState } from 'src/app/core/domain/playground-collection.model';
import { generateSnippet, SNIPPET_LANGUAGES, SnippetLanguage } from 'src/app/core/utils/code-snippet.util';
import { fetchOpenApiSpec, resolveRequestBodySchema } from 'src/app/core/utils/openapi-schema.util';
import { IonicModule } from '@ionic/angular';
import { FormsModule } from '@angular/forms';
import { JsonEditorComponent } from '../../core/components/json-editor/json-editor.component';
import { DatePipe } from '@angular/common';

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

/** `scope`/`level` mirror the HasScope(...) check the corresponding Portal.DevFunctions route
 *  actually enforces (e.g. TableStackReservationFunctions.CreateReservation requires
 *  TableStack:Write) — null scope means the route needs no token scope at all (only
 *  /openapi.json today), so it's always offered regardless of which token is selected. */
interface Endpoint { method: HttpMethod; path: string; label: string; scope: ScopeFeature | null; level: PermissionLevel; }

const LEVEL_RANK: Record<PermissionLevel, number> = { None: 0, Read: 1, Write: 2 };

interface PlaygroundResponse {
  status: number;
  statusText: string;
  durationMs: number;
  headers: { key: string; value: string }[];
  body: string;
}

/**
 * The Developer Platform's "Playground" — a real request-builder against
 * Portal.DevFunctions' curated endpoints (docs/KONZEPT-Developer-Platform.md), with
 * saved collections (tenant-shared, via /api/developer/collections), request
 * history (tenant-shared, via /api/developer/history, see PlaygroundHistoryService), and
 * generated code snippets (cURL/JS/Python/C#) for whatever is currently built.
 * Requests are sent with whichever of the user's own tokens they paste, exactly as an
 * external caller would use it — including its scope limits, so what's tested here is
 * exactly what a real integration would see.
 */
@Component({
    selector: 'app-playground',
    templateUrl: './playground.page.html',
    styleUrls: ['./playground.page.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        IonicModule,
        FormsModule,
        JsonEditorComponent,
        DatePipe,
        TranslatePipe,
    ],
})
export class PlaygroundPage implements OnInit {
  readonly endpoints: Endpoint[] = [
    { method: 'GET', path: '/company', label: 'Unternehmen: Stammdaten', scope: 'Unternehmen', level: 'Read' },
    { method: 'GET', path: '/departments', label: 'Abteilungen: auflisten', scope: 'Abteilungen', level: 'Read' },
    { method: 'GET', path: '/employees', label: 'Mitarbeitende: auflisten', scope: 'Mitarbeiter', level: 'Read' },
    { method: 'GET', path: '/tablestack/reservations', label: 'TableStack: Reservationen auflisten', scope: 'TableStack', level: 'Read' },
    { method: 'GET', path: '/tablestack/reservations/{id}', label: 'TableStack: einzelne Reservation', scope: 'TableStack', level: 'Read' },
    { method: 'POST', path: '/tablestack/reservations', label: 'TableStack: Reservation anlegen', scope: 'TableStack', level: 'Write' },
    { method: 'PATCH', path: '/tablestack/reservations/{id}', label: 'TableStack: Status ändern (Annehmen/Ablehnen/Eingetroffen)', scope: 'TableStack', level: 'Write' },
    { method: 'GET', path: '/menu/items', label: 'Speisekarte: Artikel auflisten', scope: 'Speisekarte', level: 'Read' },
    { method: 'PUT', path: '/menu/items/{id}', label: 'Speisekarte: Preis/Verfügbarkeit ändern', scope: 'Speisekarte', level: 'Write' },
    { method: 'GET', path: '/openapi.json', label: 'OpenAPI-Spezifikation', scope: null, level: 'Read' },
  ];

  readonly snippetLanguages = SNIPPET_LANGUAGES;

  tokens: DeveloperApiToken[] = [];
  selectedTokenId: string | null = null;
  selectedEndpointIndex = 0;
  method: HttpMethod = 'GET';
  path = this.endpoints[0].path;
  bodyText = '';
  pastedToken = '';

  isSending = false;
  response: PlaygroundResponse | null = null;
  requestError: string | null = null;

  /** 'response' shows the last real response; 'code' shows a generated snippet for the
   * request as currently configured — a toggle rather than two permanent panels since
   * they occupy the same space and are rarely both needed at once. */
  rightPanelView: 'response' | 'code' = 'response';
  snippetLanguage: SnippetLanguage = 'curl';

  collections: PlaygroundCollection[] = [];
  showSaveCollectionForm = false;
  newCollectionName = '';
  isSavingCollection = false;

  history: PlaygroundHistoryEntry[] = [];
  showHistory = false;

  /** Resolved JSON schema for the currently selected endpoint's request body (see
   * openapi-schema.util.ts), fed into the Monaco-based body editor for schema-driven
   * autocomplete/validation. Recomputed whenever the selected endpoint changes. Null
   * while the spec hasn't loaded yet, or for endpoints with no JSON body. */
  currentBodySchema: object | null = null;
  private openApiSpec: any = null;

  constructor(
    private http: HttpClient,
    private tokenApi: TokenApiService,
    private collectionApi: CollectionApiService,
    private historyStore: PlaygroundHistoryService,
    private toast: ToastService,
    private translate: TranslateService,
    private cdr: ChangeDetectorRef,
  ) { }

  ngOnInit(): void {
    this.tokenApi.list().subscribe(tokens => {
      this.tokens = tokens.filter(t => !t.revokedAt);
      this.selectedTokenId = this.tokens[0]?.id ?? null;
      this.syncEndpointSelectionToAuthorizedList();
      this.cdr.markForCheck();
    });

    this.loadCollections();
    this.loadHistory();

    void fetchOpenApiSpec().then(spec => {
      this.openApiSpec = spec;
      this.updateBodySchema();
      this.cdr.markForCheck();
    }).catch(() => {
      // Schema-driven autocomplete is a nice-to-have on top of the plain JSON editor —
      // a fetch failure here shouldn't block sending requests at all.
    });
  }

  onEndpointChange(index: number): void {
    this.selectedEndpointIndex = index;
    this.method = this.endpoints[index].method;
    this.path = this.endpoints[index].path;
    this.updateBodySchema();
    this.cdr.markForCheck();
  }

  /** Endpoints the currently selected token is actually scoped to call — same
   *  {feature, level} check Portal.DevFunctions' HasScope(...) enforces server-side, so the
   *  picker only ever offers requests that stand a chance of succeeding with this token
   *  (docs/KONZEPT-Developer-Platform.md). A null-scope endpoint (only /openapi.json today)
   *  needs no token at all and is always offered. */
  get visibleEndpoints(): { endpoint: Endpoint; index: number }[] {
    const token = this.tokens.find(t => t.id === this.selectedTokenId);
    return this.endpoints
      .map((endpoint, index) => ({ endpoint, index }))
      .filter(({ endpoint }) => this.isAuthorizedFor(endpoint, token));
  }

  private isAuthorizedFor(endpoint: Endpoint, token: DeveloperApiToken | undefined): boolean {
    if (!endpoint.scope) return true;
    if (!token) return false;
    const grant = token.scopes.find(s => s.feature === endpoint.scope);
    return !!grant && LEVEL_RANK[grant.level] >= LEVEL_RANK[endpoint.level];
  }

  onTokenChange(): void {
    this.syncEndpointSelectionToAuthorizedList();
    this.cdr.markForCheck();
  }

  /** Keeps the current selection valid whenever the authorized set changes (token switched,
   *  tokens finished loading): if the selected endpoint fell out of scope, jump to the first
   *  one that's still authorized rather than silently leaving an unreachable request loaded. */
  private syncEndpointSelectionToAuthorizedList(): void {
    const visible = this.visibleEndpoints;
    if (visible.some(v => v.index === this.selectedEndpointIndex)) return;
    const fallback = visible[0];
    if (!fallback) return;
    this.onEndpointChange(fallback.index);
  }

  private updateBodySchema(): void {
    this.currentBodySchema = resolveRequestBodySchema(this.openApiSpec, this.method, this.path);
  }

  methodColor(method: string): string {
    switch (method) {
      case 'GET': return 'tertiary';
      case 'POST': return 'success';
      case 'PUT': case 'PATCH': return 'warning';
      case 'DELETE': return 'danger';
      default: return 'medium';
    }
  }

  statusColor(status: number): string {
    if (status >= 500) return 'danger';
    if (status >= 400) return 'warning';
    if (status >= 300) return 'tertiary';
    if (status >= 200) return 'success';
    return 'medium';
  }

  send(): void {
    // Real tokens are only ever visible via the one-time creation reveal — the
    // Playground can select *which* token to use by name, but re-fetching its raw
    // secret is structurally impossible (it's never stored), so this control is a
    // clear TODO rather than a silent no-op: a real send needs the user to paste the
    // token they saved at creation time, exactly like any external caller would.
    if (!this.pastedToken.trim()) {
      this.requestError = this.translate.instant('playground.errors.tokenRequired');
      this.cdr.markForCheck();
      return;
    }

    this.isSending = true;
    this.response = null;
    this.requestError = null;
    this.cdr.markForCheck();

    const url = `${environment.devApiUrl}${this.path}`;
    const started = performance.now();

    this.http.request(this.method, url, {
      headers: { Authorization: `Bearer ${this.pastedToken.trim()}` },
      body: this.bodyText ? this.tryParseJson(this.bodyText) : undefined,
      observe: 'response',
      responseType: 'text',
    }).pipe(
      catchError((err: HttpErrorResponse) => of(err)),
    ).subscribe(res => {
      const durationMs = Math.round(performance.now() - started);
      const headers: { key: string; value: string }[] = [];
      res.headers.keys().forEach((k: string) => headers.push({ key: k, value: res.headers.get(k) ?? '' }));

      const isError = res instanceof HttpErrorResponse;
      const rawBody = isError ? (res.error as string ?? res.message) : (res.body ?? '');

      this.response = {
        status: res.status,
        statusText: res.statusText,
        durationMs,
        headers,
        body: this.prettyPrint(rawBody),
      };
      this.isSending = false;
      this.rightPanelView = 'response';
      this.cdr.markForCheck();

      void this.historyStore.add({
        method: this.method,
        path: this.path,
        bodyText: this.bodyText,
        status: res.status,
        durationMs,
        occurredAt: new Date().toISOString(),
      }).then(() => this.loadHistory());
    });
  }

  // ─── Code snippet ──────────────────────────────────────────────────────────

  get snippet(): string {
    const url = `${environment.devApiUrl}${this.path}`;
    const token = this.pastedToken.trim() || this.translate.instant('playground.tokenPlaceholder');
    return generateSnippet(this.snippetLanguage, this.method, url, this.bodyText, token);
  }

  async copySnippet(): Promise<void> {
    await navigator.clipboard.writeText(this.snippet);
    this.toast.success(this.translate.instant('playground.snippetCopied'));
  }

  // ─── Collections ───────────────────────────────────────────────────────────

  private loadCollections(): void {
    this.collectionApi.list().subscribe(collections => {
      this.collections = collections;
      this.cdr.markForCheck();
    });
  }

  openSaveCollectionForm(): void {
    this.showSaveCollectionForm = true;
    this.newCollectionName = '';
    this.cdr.markForCheck();
  }

  cancelSaveCollection(): void {
    this.showSaveCollectionForm = false;
    this.cdr.markForCheck();
  }

  saveCollection(): void {
    if (!this.newCollectionName.trim()) {
      this.toast.error(this.translate.instant('tokens.errors.nameRequired'));
      return;
    }

    const state: PlaygroundRequestState = { method: this.method, path: this.path, bodyText: this.bodyText };
    this.isSavingCollection = true;
    this.collectionApi.create(this.newCollectionName.trim(), JSON.stringify(state)).subscribe({
      next: () => {
        this.isSavingCollection = false;
        this.showSaveCollectionForm = false;
        this.loadCollections();
        this.toast.success(this.translate.instant('playground.collectionSaved'));
        this.cdr.markForCheck();
      },
      error: () => {
        this.isSavingCollection = false;
        this.toast.error(this.translate.instant('playground.errors.collectionSaveFailed'));
        this.cdr.markForCheck();
      },
    });
  }

  loadCollection(collection: PlaygroundCollection): void {
    try {
      const state: PlaygroundRequestState = JSON.parse(collection.requestJson);
      this.method = state.method as HttpMethod;
      this.path = state.path;
      this.bodyText = state.bodyText;
      const matchIndex = this.endpoints.findIndex(e => e.method === state.method && e.path === state.path);
      if (matchIndex >= 0) this.selectedEndpointIndex = matchIndex;
      this.updateBodySchema();
      this.cdr.markForCheck();
    } catch {
      this.toast.error(this.translate.instant('playground.errors.collectionCorrupt'));
    }
  }

  deleteCollection(collection: PlaygroundCollection): void {
    if (!confirm(this.translate.instant('playground.confirmDeleteCollection', { name: collection.name }))) return;

    this.collectionApi.delete(collection.id).subscribe({
      next: () => { this.loadCollections(); this.toast.success(this.translate.instant('playground.collectionDeleted')); },
      error: () => this.toast.error(this.translate.instant('playground.errors.collectionDeleteFailed')),
    });
  }

  // ─── History ────────────────────────────────────────────────────────────────

  toggleHistory(): void {
    this.showHistory = !this.showHistory;
    this.cdr.markForCheck();
  }

  private loadHistory(): void {
    void this.historyStore.list().then(entries => {
      this.history = entries;
      this.cdr.markForCheck();
    });
  }

  loadFromHistory(entry: PlaygroundHistoryEntry): void {
    this.method = entry.method as HttpMethod;
    this.path = entry.path;
    this.bodyText = entry.bodyText;
    const matchIndex = this.endpoints.findIndex(e => e.method === entry.method && e.path === entry.path);
    if (matchIndex >= 0) this.selectedEndpointIndex = matchIndex;
    this.updateBodySchema();
    this.cdr.markForCheck();
  }

  clearHistory(): void {
    void this.historyStore.clear().then(() => this.loadHistory());
  }

  private tryParseJson(text: string): unknown {
    try { return JSON.parse(text); } catch { return text; }
  }

  private prettyPrint(text: string): string {
    try { return JSON.stringify(JSON.parse(text), null, 2); } catch { return text; }
  }
}
